import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { toast } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { ChatRepository } from '~/repositories/chatRepository';
import type {
  ChatMessageDto,
  ChatTypes,
  CreatePhoneChatDto,
  EntityType,
  HydratedMagicChatDto,
  PropertyManagerDto,
  UpdateChatDto,
} from '~/swagger/portal/Api';
export const [ChatProvider, useChat] = createMagicDoorContext('Chat', () => {
  const repo = new ChatRepository();
  const [isSearchMode, setIsSearchMode] = createSignal<boolean>(false);
  const [chat, setChat] = createSignal<HydratedMagicChatDto>();
  const [chatId, setChatId] = createSignal<string | undefined>(undefined);
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);

  const [isInitialized, setIsInitialized] = createSignal<boolean>(false);

  const initializeChat = async (id: string, initChatMessage?: (chatId: string) => Promise<void>) => {
    if (!isInitialized() || chatId() !== id) {
      setIsLoading(true);
      try {
        const chatData = await fetchChatById(id);

        setChatId(id);
        setChat(chatData);
        await initChatMessage?.(id);
        setIsInitialized(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetChat = (clearMessage?: () => void) => {
    setChatId(undefined);
    setChat(undefined);
    clearMessage?.();
    setIsInitialized(false);
  };

  const fetchChatById = async (chatId: string): Promise<HydratedMagicChatDto> => {
    setIsLoading(true);
    try {
      const chatData = await repo.getChat(chatId);
      if (!chatData) {
        throw new Error('Chat data not found');
      }
      setChat(chatData);

      return chatData;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const createChat = async (entryType: `${EntityType}` | `${ChatTypes}`, entryId: string) => {
    setIsLoading(true);
    try {
      const response = await repo.createChatWindow(entryType, entryId);
      if (response && response.chatId) {
        return { chatId: response.chatId };
      } else {
        toast.error(response.failureReason);
        throw new Error('Failed to create a new chatMessages. No chatId returned.');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const createTextChat = async (request: CreatePhoneChatDto) => {
    setIsLoading(true);
    try {
      const response = await repo.createTextMessageChat(request);
      if (response && response.chatId) {
        return { chatId: response.chatId };
      } else {
        toast.error(response.failureReason);
        throw new Error('Failed to create a new chatMessages. No chatId returned.');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const editChat = async (chatId: string, chatData: UpdateChatDto): Promise<HydratedMagicChatDto | undefined> => {
    setIsLoading(true);
    try {
      const response = await repo.updateChat(chatId, chatData);
      setChat(response);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const applyHumanEngagement = (id: string, requested: boolean, requestedAt?: string | null) => {
    setChat((prev) => {
      if (!prev || prev.id !== id) return prev;
      return { ...prev, humanEngagementRequested: requested ? (requestedAt ?? todayWithSystemTimeZone().toISOString()) : undefined };
    });
  };

  const setHumanEngagement = async (id: string, requested: boolean): Promise<boolean> => {
    try {
      await repo.setHumanEngagement(id, requested);
      applyHumanEngagement(id, requested);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    }
  };

  const applyChatAssignment = (
    id: string,
    assignedPropertyManager: PropertyManagerDto | null,
    assignedByPropertyManager: PropertyManagerDto | null,
    assignedAt: string | null
  ) => {
    setChat((prev) => {
      if (!prev || prev.id !== id) return prev;
      return {
        ...prev,
        assignedPropertyManager: assignedPropertyManager ?? undefined,
        assignedByPropertyManager: assignedByPropertyManager ?? undefined,
        assignedAt: assignedAt ?? undefined,
      };
    });
  };

  const assignChat = async (id: string, propertyManagerId: string | null): Promise<HydratedMagicChatDto | undefined> => {
    setIsLoading(true);
    try {
      const response = await repo.setChatAssignee(id, propertyManagerId);
      setChat(response);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const markMessagesAsRead = async (chatId: string) => {
    try {
      await repo.markMessagesAsRead(chatId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  function getParticipantName(chatMessage: ChatMessageDto, chatDetails: HydratedMagicChatDto): string | undefined | null {
    if (chatMessage.participantId && chatDetails.participants) {
      const participant = chatDetails.participants.find((p) => p.id === chatMessage.participantId);
      return participant ? participant.name : undefined;
    }
    return undefined;
  }

  const archiveChat = async (chatId: string) => {
    setIsLoading(true);
    try {
      const response = await repo.archiveChat(chatId);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    chat,
    isSearchMode,
    setIsSearchMode,
    chatId,
    isLoading,
    setIsLoading,
    error,
    setError,
    isInitialized,
    initializeChat,
    resetChat,
    fetchChatById,
    createChat,
    createTextChat,
    editChat,
    markMessagesAsRead,
    getParticipantName,
    archiveChat,
    setChatId,
    setHumanEngagement,
    applyHumanEngagement,
    assignChat,
    applyChatAssignment,
  };
});
